/**
 * JokerBar - Quiz Joker/Lifeline Buttons Component
 * 
 * Displays available jokers with their status and handles joker activation.
 * 
 * Features:
 * - Visual indicators for available/used jokers
 * - Loading states during joker activation
 * - Tooltips with joker descriptions
 * - Responsive layout (horizontal/vertical)
 */

import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { JOKER_TYPES } from '../hooks/useJokers';

/**
 * Joker configuration with icons and labels
 */
const JOKER_CONFIG = {
  [JOKER_TYPES.FIFTY_FIFTY]: {
    label: '50:50',
    description: 'Entfernt 2 falsche Antworten',
    icon: (props) => (
      <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M12 4v16m8-8H4" />
        <text x="4" y="10" fontSize="8" fill="currentColor">50</text>
        <text x="14" y="20" fontSize="8" fill="currentColor">50</text>
      </svg>
    ),
    textIcon: '50:50',
    color: 'indigo',
  },
  [JOKER_TYPES.AUDIENCE]: {
    label: 'Publikum',
    description: 'Zeigt die Antwortverteilung der anderen Teilnehmer',
    icon: (props) => (
      <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    color: 'blue',
  },
  [JOKER_TYPES.BEST_PLAYER]: {
    label: 'Beste/r',
    description: 'Zeigt die Antwort des führenden Spielers',
    icon: (props) => (
      <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'yellow',
  },
  [JOKER_TYPES.TIME_EXTENSION]: {
    label: '+20s',
    description: 'Verlängert die Zeit um 20 Sekunden',
    icon: (props) => (
      <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'green',
  },
};

/**
 * Color mappings for joker states
 */
const getColorClasses = (color, isAvailable, darkMode) => {
  if (!isAvailable) {
    return {
      bg: darkMode ? 'bg-gray-800' : 'bg-gray-200',
      text: darkMode ? 'text-gray-600' : 'text-gray-400',
      border: darkMode ? 'border-gray-700' : 'border-gray-300',
      hover: '',
    };
  }

  const colors = {
    indigo: {
      bg: darkMode ? 'bg-indigo-900/30' : 'bg-indigo-50',
      text: darkMode ? 'text-indigo-400' : 'text-indigo-600',
      border: darkMode ? 'border-indigo-700' : 'border-indigo-300',
      hover: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/50',
    },
    blue: {
      bg: darkMode ? 'bg-blue-900/30' : 'bg-blue-50',
      text: darkMode ? 'text-blue-400' : 'text-blue-600',
      border: darkMode ? 'border-blue-700' : 'border-blue-300',
      hover: 'hover:bg-blue-100 dark:hover:bg-blue-900/50',
    },
    yellow: {
      bg: darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50',
      text: darkMode ? 'text-yellow-400' : 'text-yellow-600',
      border: darkMode ? 'border-yellow-700' : 'border-yellow-300',
      hover: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/50',
    },
    green: {
      bg: darkMode ? 'bg-green-900/30' : 'bg-green-50',
      text: darkMode ? 'text-green-400' : 'text-green-600',
      border: darkMode ? 'border-green-700' : 'border-green-300',
      hover: 'hover:bg-green-100 dark:hover:bg-green-900/50',
    },
  };

  return colors[color] || colors.indigo;
};

/**
 * Single Joker Button
 */
export function JokerButton({
  type,
  isAvailable = true,
  isLoading = false,
  onClick,
  showLabel = true,
  size = 'md',
  showTooltip = true,
}) {
  const { darkMode } = useTheme();
  const config = JOKER_CONFIG[type];
  
  if (!config) return null;

  const colors = getColorClasses(config.color, isAvailable, darkMode);

  // Size variants
  const sizeClasses = {
    sm: {
      button: 'px-2 py-1.5',
      icon: 'w-4 h-4',
      text: 'text-xs',
    },
    md: {
      button: 'px-3 py-2',
      icon: 'w-5 h-5',
      text: 'text-sm',
    },
    lg: {
      button: 'px-4 py-3',
      icon: 'w-6 h-6',
      text: 'text-base',
    },
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  const Icon = config.icon;

  return (
    <button
      onClick={() => isAvailable && !isLoading && onClick?.(type)}
      disabled={!isAvailable || isLoading}
      className={`
        ${sizes.button}
        ${colors.bg}
        ${colors.text}
        ${colors.hover}
        border ${colors.border}
        rounded-lg
        flex items-center gap-2
        font-medium
        transition-all duration-200
        ${isAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
        ${!isAvailable ? 'line-through' : ''}
        relative group
      `}
      title={showTooltip ? config.description : undefined}
    >
      {/* Loading spinner */}
      {isLoading ? (
        <svg
          className={`${sizes.icon} animate-spin`}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : config.textIcon ? (
        <span className={`${sizes.text} font-bold`}>{config.textIcon}</span>
      ) : (
        <Icon className={sizes.icon} />
      )}

      {showLabel && (
        <span className={sizes.text}>{config.label}</span>
      )}

      {/* Used indicator */}
      {!isAvailable && !isLoading && (
        <svg
          className={`absolute -top-1 -right-1 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </button>
  );
}

/**
 * JokerBar - Container for all joker buttons
 * Note: Time Extension joker has been removed - timer is now controlled by admin
 */
export function JokerBar({
  availableJokers = {},
  jokerLoading = {},
  onUseFiftyFifty,
  onUseAudience,
  onUseBestPlayer,
  onUseTimeExtension, // Kept for backward compatibility but not used
  disabled = false,
  layout = 'horizontal',
  size = 'md',
  showLabels = true,
}) {
  const { darkMode } = useTheme();

  const handleJokerClick = (type) => {
    if (disabled) return;

    switch (type) {
      case JOKER_TYPES.FIFTY_FIFTY:
        onUseFiftyFifty?.();
        break;
      case JOKER_TYPES.AUDIENCE:
        onUseAudience?.();
        break;
      case JOKER_TYPES.BEST_PLAYER:
        onUseBestPlayer?.();
        break;
      // Time extension joker removed - controlled by admin now
    }
  };

  // Time extension joker removed from available types
  const jokerTypes = [
    JOKER_TYPES.FIFTY_FIFTY,
    JOKER_TYPES.AUDIENCE,
    JOKER_TYPES.BEST_PLAYER,
  ];

  return (
    <div
      className={`
        ${layout === 'horizontal' ? 'flex flex-wrap gap-2 justify-center' : 'flex flex-col gap-2'}
        ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}
        p-3 rounded-xl
      `}
    >
      {/* Header */}
      <div className={`w-full text-center mb-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Joker
      </div>

      {/* Joker buttons */}
      <div className={`flex ${layout === 'horizontal' ? 'flex-wrap gap-2 justify-center' : 'flex-col gap-2'}`}>
        {jokerTypes.map(type => (
          <JokerButton
            key={type}
            type={type}
            isAvailable={availableJokers[type] && !disabled}
            isLoading={jokerLoading[type]}
            onClick={handleJokerClick}
            showLabel={showLabels}
            size={size}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Compact joker indicator (for header use)
 */
export function JokerIndicator({ availableJokers = {} }) {
  const { darkMode } = useTheme();
  const availableCount = Object.values(availableJokers).filter(Boolean).length;

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm
        ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}
      `}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      {availableCount}/4
    </span>
  );
}

export default JokerBar;
